import { useSite } from "@/lib/SiteContext";
import { useT } from "@/lib/i18n";
import Reveal from "./Reveal";

const IMG = {
  CO: "https://images.pexels.com/photos/13767911/pexels-photo-13767911.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  MX: "https://images.pexels.com/photos/34147424/pexels-photo-34147424.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  ES: "https://images.pexels.com/photos/34990522/pexels-photo-34990522.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  GL: "https://images.pexels.com/photos/36055561/pexels-photo-36055561.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
};

const COORDS = {
  CO: "04°35′N · 74°04′W",
  MX: "19°25′N · 99°08′W",
  ES: "40°25′N · 03°42′W",
  GL: "—°—°—",
};

const GRADS = {
  CO: "linear-gradient(135deg, #FF1F8F 0%, #FCB424 100%)",
  MX: "linear-gradient(135deg, #FCB424 0%, #8AE52E 100%)",
  ES: "linear-gradient(135deg, #8AE52E 0%, #2DD4BF 100%)",
  GL: "linear-gradient(135deg, #FF1F8F 0%, #8AE52E 100%)",
};

export const Cobertura = () => {
  const { locale } = useSite();
  const { t } = useT(locale);
  const dests = t("cobertura.destinations");

  return (
    <section
      id="cobertura"
      className="relative py-28 md:py-36"
      style={{ backgroundColor: "var(--ix-cream)", color: "var(--ix-night)" }}
      data-testid="cobertura-section"
    >
      <div className="max-w-[1480px] mx-auto px-5 md:px-10 lg:px-14">
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
            <div className="lg:col-span-4">
              <div className="ix-mono text-[0.7rem] uppercase tracking-[0.18em] flex items-center gap-2">
                <span className="ix-grad-text font-semibold">◆</span>
                {t("cobertura.eyebrow")}
              </div>
            </div>
            <div className="lg:col-span-8">
              <h2 className="ix-display ix-headline-lg">
                {t("cobertura.title")}
              </h2>
              <p className="mt-5 text-[1rem] leading-relaxed text-[var(--ix-night)]/70 max-w-2xl">
                {t("cobertura.lead")}
              </p>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {dests.map((d, i) => (
            <Reveal
              key={d.code}
              delay={i * 90}
              className="ix-card-light ix-sweep group overflow-hidden"
              data-testid={`destination-${d.code}`}
            >
              <div className="ix-img-zoom relative aspect-[3/4] overflow-hidden">
                <img
                  src={IMG[d.code]}
                  alt={d.name}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[var(--ix-night)]/15 group-hover:bg-[var(--ix-night)]/0 transition-colors duration-700" />
                {/* code badge */}
                <div
                  aria-hidden
                  className="absolute top-3 left-3"
                  style={{
                    width: 46, height: 58,
                    background: GRADS[d.code],
                    clipPath: "polygon(22% 0%, 100% 0%, 78% 100%, 0% 100%)",
                  }}
                />
                <div className="absolute top-[18px] left-[16px] ix-display text-[0.92rem] font-bold text-[var(--ix-night)] z-10 mix-blend-overlay">
                  {d.code}
                </div>
                {/* pulse dot */}
                <div className="absolute bottom-4 right-4">
                  <div
                    className="ix-pulse-ring relative w-3 h-3 rounded-full"
                    style={{ background: GRADS[d.code] }}
                  />
                </div>
                <div className="absolute bottom-3 left-4 ix-mono text-[0.6rem] uppercase tracking-[0.18em] text-[var(--ix-cream)]">
                  {COORDS[d.code]}
                </div>
              </div>
              <div className="p-5">
                <div className="ix-display text-[1.6rem] text-[var(--ix-night)] leading-tight">
                  {d.name}
                </div>
                <div className="mt-2 text-[0.86rem] text-[var(--ix-muted)]">{d.note}</div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Markets + Fairs — international promotion track record */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <Reveal data-testid="cobertura-markets">
            <div className="ix-mono text-[0.7rem] uppercase tracking-[0.2em] text-[var(--ix-night)]/55 flex items-center gap-2 mb-5">
              <span className="ix-grad-text font-semibold">▸</span>
              {t("cobertura.markets_label")}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[var(--ix-night)]/10">
              {t("cobertura.markets").map((m) => (
                <div
                  key={m.code}
                  className="p-4 bg-[var(--ix-cream)] flex flex-col items-start gap-1"
                  data-testid={`market-${m.code}`}
                >
                  <span className="ix-mono text-[0.66rem] tracking-[0.18em] ix-grad-text font-semibold">
                    {m.code}
                  </span>
                  <span className="ix-display text-[1.05rem] text-[var(--ix-night)] leading-tight">
                    {m.name}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={120} data-testid="cobertura-fairs">
            <div className="ix-mono text-[0.7rem] uppercase tracking-[0.2em] text-[var(--ix-night)]/55 flex items-center gap-2 mb-5">
              <span className="ix-grad-text font-semibold">▸</span>
              {t("cobertura.fairs_label")}
            </div>
            <ul className="space-y-3">
              {t("cobertura.fairs").map((f, i) => (
                <li
                  key={f}
                  className="flex items-start gap-3 ix-display text-[1.05rem] text-[var(--ix-night)] leading-snug pb-3 border-b border-[var(--ix-night)]/10"
                  data-testid={`fair-${i}`}
                >
                  <span className="ix-mono text-[0.7rem] text-[var(--ix-night)]/45 mt-[7px] flex-shrink-0">
                    ◆
                  </span>
                  <span style={{ fontWeight: 500 }}>{f}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Cobertura;
